import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { StatusBar } from 'expo-status-bar'
import type { ReactNode } from 'react'
import { Pressable, StyleSheet, Text, TouchableOpacity, View, ViewStyle } from 'react-native'
import { IconButton, ProgressBar as PaperProgressBar, PaperProvider, Icon } from 'react-native-paper'
import Animated, {
  Easing,
  FadeInDown,
  FadeInUp,
  FadeOutDown,
  interpolate,
  SlideInRight,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming
} from 'react-native-reanimated'
import { create } from 'zustand'
import { CardString, Hand } from 'pokersolver'
import { useThrottledCallback } from 'use-debounce'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { createComponent } from 'react-native-small-ui'
import { useNavigation, useRouter } from 'expo-router'
import Toast, { ToastShowParams } from 'react-native-toast-message'
import { observer } from 'mobx-react-lite'

import { isWeb, useMaxWidth, WebWrapper } from '@/components/WebWrapper'
import { DS } from '@/design-system'
import { getDSColorWithOpacity } from '@/design-system/Palette'
import { useHandHistoryQuery } from '@/hooks/convex/useGameState'
import { useGameContext } from './GameContext'
import { useSpectatorStatus } from './useSpectatorStatus'

import { TimerDebugPanel } from '@/components/GameAssets/TimerDebugPanel'
import { TeacherControlsPanel } from '@/components/GameAssets/TeacherControlsPanel'
import { Id } from '@ppwr/convex/_generated/dataModel'
import { GAME_CONSTANTS } from '@ppwr/convex/shared/constants'
import { Draggable } from '@/components/GameAssets/Draggable/Draggable'
import { useGamePanelsStore } from './useGamePanelsStore'
import { useMutation } from 'convex/react'
import { PauseSVG } from '@/components/ANewDesign/TeacherControl/assets/PauseSVG'
import { PlaySVG } from '@/components/ANewDesign/TeacherControl/assets/PlaySVG'
import { SettingsSVG } from '@/components/ANewDesign/TeacherControl/assets/SettingsSVG'

// Daily10 constants
const { DEFAULT_MAX_HANDS, DEFAULT_FREE_PREFLOP_FOLDS } = GAME_CONSTANTS.DAILY10_CONSTANTS

import { SeatAlignment, PlayerSeats, SeatContentFunction } from '@/components/GameAssets/Table/Table'
import { useTableLayoutPosition } from '@/components/GameAssets/Table/useTableLayout'
import { userIdToAgoraUid } from '@/hooks/av/uid'
import { useAVContext } from '@/hooks/av'
import { formatChipsNumber, PlayerBox } from '@/components/GameAssets/Player/PlayerBox'
import { PlayerController } from '@/components/GameAssets/PlayerController'
import { usePlayerActions } from '@/hooks/convex/usePlayerActions'

import PokerPowerLogo from '@/assets/pokerpower-logo.svg'
import { TeacherSeat } from '@/components/ANewDesign/Board/TeacherSeat'
import { GameBottomSheet } from '@/screens/GameContainer/GameBottomSheet/GameBottomSheet'
import { useGameBottomSheetStore } from '@/screens/GameContainer/GameBottomSheet/useGameBottomSheetStore'
import { PlayerRole, type PlayerRole as PlayerRoleType } from '@/components/ANewDesign/PlayerRole'
import { useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { DEFAULT_BOT_SKILL_LEVEL } from '@ppwr/poker-engine/utils'
import { BOT_SKILL_COLORS } from '../../utils/botSkillColors'
import { StartGameSheet } from './StartGameSheet'
import { StartingInSheet } from './StartingInSheet'
import { WaitingForPlayersSheet } from './WaitingForPlayersSheet'
import { WaitingForHostSheet } from './WaitingForHostSheet'
import { GameModals } from './GameModals'
import { JoinConfirmModal } from './JoinConfirmModal'
import { TeacherSeatChoiceModal } from './TeacherSeatChoiceModal'
import { GameContainerContext, LoungeThemeContext } from './GameContext'
import { AVButton } from '../../components/AV/AVButton'
import { AVCallProvider } from '../../components/AV/AVCallProvider'
import { useGameState } from './useGameState'
import { PlayerActions } from './PlayerActions'
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet'
import { getLastActionTextForPlayerConvex } from './utils/convexActions'
import { translate } from '../../i18n'
import { useStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'
import { useChatMessages } from '@/hooks/convex/useChatMessages'

import { useCheatSheet } from '../../components/CheatSheet/useCheatSheet'
import { useAutoPauseOnBackground } from './hooks/useAutoPauseOnBackground'
import { playSound } from '../../utils/audioService'
import { SanitizedPlayer } from '@ppwr/convex/shared/types'
import { PlayerBetChips } from '../../components/GameAssets/Player/PlayerBetChips'
import { TableContainer } from './TableContainer'
import { BlurView } from 'expo-blur'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import { scheduleOnRN } from 'react-native-worklets'
import { ChatLogsSheet, useChatLogsSheet } from './ChatLogsSheet'
import { SidePotSheet, useSidePotSheet } from './SidePotSheet'
import { SidePotWinnersSheet } from './SidePotWinnersSheet'
import { useGameOptionsDrawerStore } from './GameOptionsDrawer'
import { DebugPanel } from '../../components/GameAssets/DebugPanel'
import { GameContentWithDrawer } from './GameContentWithDrawer'
import { isCelebrationMessage, parseCelebrationMessage, useCelebration } from '../new-design/hooks/useCelebration'
import { useHandWinners } from '../../hooks/convex/useHandWinners'
import { CommandsContextProvider } from '../new-design/hooks/useCommonCommands'
import { useNewMessages } from '../../hooks/convex/useNewMessages'
import { useMessageStore } from '../../hooks/convex/useMessageStore'
import { useCelebrationMessages } from '../../hooks/convex/useCelebrationMessages'
import { useActiveFlowSchedule } from '../../hooks/convex/useActiveFlowSchedule'
import { useListenGameToastNotificationsConvex } from '../new-design/hooks/useListenGameToastNotificationsConvex'
import { ToastNotification } from '@/components/ANewDesign/ToastNotification/ToastNotification'
import { TournamentHUD } from '@/screens/tournament/TournamentHUD'

import { spectatorReturnToTables } from '@/screens/tournament/mttNavigation'
import { GameHUD } from './GameHUD'
import { ActionAdvisorCard, ADVISOR_AVATAR_HOST } from './ActionAdvisorCard'
import { useActionAdvisor } from './useActionAdvisor'
import { useTurnAdvice } from './useTurnAdvice'
import { getAdvisorCoachingLevel, useActionAdvisorStore } from './useActionAdvisorStore'
import { getDisplayedAdvisorFeedback } from './actionAdvisor.helpers'
import { didFoldedPlayerHaveWinningHand, didPlayerOnlyFoldPreflop, didUserWinHand } from './actionAdvisor.outcome'
import { PortalHost } from '@gorhom/portal'

import { DiamondCountDisplay } from '@/components/Diamonds/Diamonds'

const Center = createComponent(View, {
  alignItems: 'center',
  justifyContent: 'center'
})
const Box = createComponent(View, {
  flexGrow: 1
})
const Content = createComponent(View, {
  flexDirection: 'column',
  flexGrow: 1,
  justifyContent: 'flex-end'
})
const Row = createComponent(View, {
  flexDirection: 'row'
})

const useIsDebugModeEnabled = () => {
  // const isAdmin = true // useStore().session
  const isAdmin = !!useStore().session.user?.userRoles?.includes('admin')
  return process.env.EXPO_PUBLIC_STAGE === 'dev' || __DEV__ || isAdmin
}

interface GameContainerProps {
  gameId: Id<'games'>
  userId: string
  pendingJoin?: boolean
  joinCode?: string
}

const GameContainerProvider = ({
  children,
  gameId,
  userId
}: React.PropsWithChildren<Omit<GameContainerProps, 'pendingJoin' | 'joinCode'>>) => {
  const value = useMemo(() => ({ gameId, userId }), [gameId, userId])
  return (
    <GameContainerContext.Provider value={value}>
      <BottomSheetModalProvider>
        <PaperProvider>
          <AVCallProvider>{children}</AVCallProvider>
        </PaperProvider>
      </BottomSheetModalProvider>
    </GameContainerContext.Provider>
  )
}

export const GameContainer = ({ pendingJoin, joinCode, ...props }: GameContainerProps) => {
  return (
    <GameContainerProvider {...props}>
      <GameContentWithDrawer>
        <CommandsContextProvider context="convex">
          <GameContent />
        </CommandsContextProvider>
      </GameContentWithDrawer>
      {pendingJoin && joinCode && <JoinConfirmModal gameId={props.gameId} userId={props.userId} code={joinCode} />}
    </GameContainerProvider>
  )
}

// NOTE: DEV store
const useGameDevStore = create(() => ({
  showDebug: false,
  bigBoard: false,
  isPlayerSeated: true,
  pauseOverlay: false,
  canPlayerResume: true,
  showLogoAndBorder: true
}))

//#region GameDebug - Debug Data Panel (Admin Only)
const GameDebugPanel = () => {
  const insets = useSafeAreaInsets()
  const { showDebugPanel } = useGamePanelsStore()
  const { game, players } = useGameState()
  const { handHistory } = useHandHistoryQuery(game?._id)

  if (!showDebugPanel) {
    return null
  }

  return (
    <Box position="absolute" top={insets.top} zIndex={99}>
      <Draggable>
        <DebugPanel
          gameData={game}
          playersData={players}
          handHistoryData={handHistory}
          initiallyCollapsed={false}
          maxHeight={300}
        />
      </Draggable>
    </Box>
  )
}

//#region TimerDebug - Timing Panel (Admin Only)
const TimingPanel = () => {
  const { showTimingPanel } = useGamePanelsStore()
  const { gameId } = useGameContext()
  const { game } = useGameState()

  const shouldShow = showTimingPanel && gameId
  if (!shouldShow) {
    return null
  }

  return <TimerDebugPanel game={game} gameId={gameId} />
}

//#region TeacherControls - Teacher Controls Panel (Admin + Teachers)
const TeacherControls = () => {
  const { showTeacherControls } = useGamePanelsStore()
  const { gameId } = useGameContext()
  const { game } = useGameState()

  const shouldShow = showTeacherControls && gameId
  if (!shouldShow) {
    return null
  }

  return <TeacherControlsPanel game={game} gameId={gameId} />
}

//#region ConfigPanel
const GameplayConfigPanel = () => {
  const state = useGameDevStore()
  const layout = useTableLayoutPosition()

  const defaultTranslateY = layout.header.height + layout.seats.height * 2 + layout.seatsLayout.topSpacer

  const minWidth = 120
  const iconSize = 18

  const animationProgress = useSharedValue(0)
  const translateY = useSharedValue(defaultTranslateY)
  const contextY = useSharedValue(0)

  const [isOpen, setIsOpen] = useState(false)

  useAnimatedReaction(
    () => animationProgress.value,
    (latestValue) => {
      // Run on the JS thread to update React state
      scheduleOnRN(setIsOpen, latestValue < 1)
    },
    [animationProgress]
  )

  const panGesture = Gesture.Pan()
    .onStart(() => {
      contextY.value = translateY.value
    })
    .onUpdate((event) => {
      translateY.value = contextY.value + event.translationY
    })
    .onEnd(() => {
      // Optional: Add bounce effect when released
      translateY.value = withSpring(translateY.value)
    })

  const handlePress = () => {
    animationProgress.value = withTiming(animationProgress.value === 0 ? 1 : 0, {
      duration: 250,
      easing: Easing.bezier(0.25, 0.1, 0.25, 1)
    })
  }

  const animatedStyle = useAnimatedStyle(() => {
    const translateX = interpolate(animationProgress.value, [0, 1], [0, minWidth * -1])
    return {
      transform: [{ translateX }, { translateY: translateY.value }]
    }
  }, [])

  return (
    <GestureDetector gesture={panGesture}>
      <Animated.View
        style={[
          {
            position: 'absolute',
            zIndex: 99
          },
          animatedStyle
        ]}
      >
        <Box
          position="absolute"
          right={-iconSize}
          width={iconSize}
          flexGrow={0}
          backgroundColor="#000b"
          style={{
            borderWidth: 1,
            borderLeftWidth: 0,
            borderColor: '#fff7',
            borderTopRightRadius: 8,
            borderBottomRightRadius: 8
          }}
        >
          <Pressable
            onPress={handlePress}
            style={{
              width: iconSize,
              height: iconSize * 1.5,
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.8
            }}
          >
            {isOpen ? (
              <Icon source="chevron-left" size={16} color={'#FFF'} />
            ) : (
              <Icon source="chevron-right" size={16} color={'#FFF'} />
            )}
          </Pressable>
        </Box>
        {/* Panel */}
        <Box
          backgroundColor="#000b"
          borderBottomRightRadius={6}
          minWidth={minWidth}
          minHeight={64}
          maxWidth={minWidth}
          padding={4}
          paddingVertical={8}
          gap={4}
          style={{
            borderTopWidth: 1,
            borderColor: '#fff7'
          }}
        >
          <ConfigPanelLabel title="Display Settings" />
          <ConfigPanelToggle
            title="Observer View"
            value={!state.isPlayerSeated}
            onPress={() => {
              useGameDevStore.setState((s) => ({
                isPlayerSeated: !s.isPlayerSeated
              }))
            }}
          />
          <ConfigPanelToggle
            title="Big Board"
            value={state.bigBoard}
            onPress={() => {
              useGameDevStore.setState((s) => ({
                bigBoard: !s.bigBoard
              }))
            }}
          />
          <ConfigPanelToggle
            title="Player Resume"
            value={state.canPlayerResume}
            onPress={() => {
              useGameDevStore.setState((s) => ({
                canPlayerResume: !s.canPlayerResume
              }))
            }}
          />
          <ConfigPanelToggle
            title="Pause Overlay"
            value={state.pauseOverlay}
            onPress={() => {
              useGameDevStore.setState((s) => ({
                pauseOverlay: !s.pauseOverlay
              }))
            }}
          />
          <ConfigPanelToggle
            title="Debug Mode"
            value={state.showDebug}
            onPress={() => {
              useGameDevStore.setState((s) => ({
                showDebug: !s.showDebug
              }))
            }}
          />
        </Box>
      </Animated.View>
    </GestureDetector>
  )
}

const ConfigPanelToggle = ({ title, value, onPress }: { title: string; value: boolean; onPress: () => void }) => {
  return (
    <Pressable onPress={onPress}>
      <Row gap={4}>
        <Text style={{ color: '#fff', fontSize: 9, lineHeight: 14, textTransform: 'uppercase' }}>{title}</Text>
        {value ? (
          <Icon source={'toggle-switch-outline'} size={14} color="#0ff" />
        ) : (
          <Icon source={'toggle-switch-off-outline'} size={14} color="#eee" />
        )}
      </Row>
    </Pressable>
  )
}

const ConfigPanelLabel = ({ title }: { title: string }) => {
  return (
    <Row gap={4} style={{ borderBottomWidth: 1, borderBottomColor: '#fff4', paddingBottom: 2 }}>
      <Text style={{ color: '#fff', fontSize: 9, lineHeight: 14, textTransform: 'uppercase' }}>{title}</Text>
    </Row>
  )
}

const PauseOverlay = () => {
  const insets = useSafeAreaInsets()
  const layout = useTableLayoutPosition()
  const state = useGameDevStore()

  if (!state.pauseOverlay) {
    return null
  }

  return (
    <View
      style={{
        position: 'absolute',
        zIndex: 98,
        top: insets.top * -1 - 4,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#0006'
      }}
    >
      <BlurView tint="regular" intensity={1} style={{ flexGrow: 1 }}>
        <Center
          flexGrow={1}
          // marginTop={insets.top + layout.seats.height + layout.seatsLayout.topSpacer / 2}
          //
        >
          <BlurView tint="regular" intensity={15}>
            <View
              style={{
                borderRadius: 4,
                backgroundColor: `${DS.palette['Brand/Maroon']}77`,
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <DS.TextScaled
                variant="Body Large/Regular"
                color="Global/White"
                uppercase
                style={{
                  letterSpacing: 1,
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                  //
                  shadowColor: '#000',
                  shadowOffset: {
                    width: 0,
                    height: 0
                  },
                  shadowOpacity: 1,
                  shadowRadius: 1,

                  elevation: 2
                }}
              >
                Game is Paused
              </DS.TextScaled>
            </View>
          </BlurView>
        </Center>
      </BlurView>
    </View>
  )
}

//#region GameContent
const GameContent = observer(() => {
  // TODO: game ended detection
  const insets = useSafeAreaInsets()
  const layout = useTableLayoutPosition()
  const { renderEmojis, clearEmojis, count, triggerLocalCelebration } = useCelebration()

  const { isPaused, game, myPlayer, players, loungeTableColor } = useGameState()
  const { gameId, userId: contextUserId } = useGameContext()
  const { isSpectator: isSpectatingServer } = useSpectatorStatus(gameId, contextUserId)
  const isPracticeGame = game?.mode === 'practice'
  const isDaily10 = game?.mode === 'daily10'
  const isTournament = game?.mode === 'mtt'
  const navigation = useNavigation()

  const isFocused = navigation.isFocused()
  useEffect(() => {
    if (isFocused) {
      clearEmojis()
    }
  }, [isFocused, clearEmojis])

  // Auto-pause game when app is backgrounded or closing
  useAutoPauseOnBackground()

  // Toast when teacher is viewing cards (allPrivate / allPublic / byPlayer)
  useListenGameToastNotificationsConvex()

  // TODO REMOVE (actually) TWEAK BEFORE MERGE PoC: Show the last convex based message
  const { messages: allMessages } = useChatMessages(game?._id)

  // Handle any new celebrations
  useCelebrationMessages(allMessages)

  // Filter to only the new messages then show in a toast
  const newMessages = useNewMessages(allMessages, game?._id)
  const newestMessageId = newMessages?.[0]?._id
  useEffect(() => {
    const msg = newMessages?.[0]
    if (!msg) return

    let toast: ToastShowParams | undefined
    switch (msg.chatKind) {
      case 'user':
        toast = {
          type: 'info',
          text1: `${msg.userName} says`,
          text2: `${msg.message}`
        }
        break
      case 'engine':
        if (isCelebrationMessage(msg.message)) {
          const player = players.find((p) => p.userId === msg.userId)
          const emoji = parseCelebrationMessage(msg.message)
          // Observers have no `players` row, so the roster lookup misses them;
          // fall back to the name stamped on the chat message at send time.
          const senderName = player?.name || msg.userName || 'a Fan'
          toast = {
            type: 'success',
            text1: myPlayer?.userId === msg.userId ? 'Sent!' : `Congrats from ${senderName} ${emoji}`
          }
        }
        break
      default:
        toast = {
          type: 'error',
          text1: 'Unknown message',
          text2: `${msg.message}`
        }
    }
    if (toast) {
      Toast.show(toast)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newestMessageId])

  // Cleanup message store timestamp when leaving game
  // TODO: Add TTL-based automatic cleanup for old game timestamps to prevent memory growth
  const prevGameIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const currentGameId = game?._id
    const prevGameId = prevGameIdRef.current

    // Clean up previous game when switching to a different game
    if (prevGameId && prevGameId !== currentGameId) {
      useMessageStore.getState().clearGame(prevGameId)
    }

    // Track current game ID for next render
    prevGameIdRef.current = currentGameId

    // Clean up current game on unmount
    return () => {
      if (currentGameId) {
        useMessageStore.getState().clearGame(currentGameId)
      }
    }
  }, [game?._id])

  const { openSheet, closeSheet } = useGameBottomSheetStore()
  const onCloseSheetCallback = useCallback(() => {
    closeSheet()
  }, [closeSheet])

  const { isNewHand, latestHand, handNumber, isUserWinner } = useHandWinners()
  const { isWinnersReveal = false, remainingMs = 8000 } = useActiveFlowSchedule(game?._id, isPaused) || {}
  const advisorEnabled = useActionAdvisorStore((state) => state.enabled)
  const advisorFeedbackEnabled = useActionAdvisorStore((state) => state.feedbackEnabled)
  const queueHandOutcomeFeedback = useActionAdvisorStore((state) => state.queueHandOutcomeFeedback)

  // The ShowCards drawer doubles as the winners-reveal panel: it shows the
  // "show your hole cards" prompt for seated players who were dealt in, and
  // for everyone else (spectators, busted players) it still renders the
  // celebration emojis + "next hand in" timer with the cards section hidden.
  // The cards section itself is gated inside `ShowCards` by `hasCards`.
  // Practice and daily10 modes handle reveals their own way.
  const canShowCards = !isPracticeGame && !isDaily10
  // NOTE: this linter error about remainingMs is OK.
  useEffect(() => {
    if (canShowCards && isWinnersReveal) {
      openSheet('ShowCards', {
        onClose: onCloseSheetCallback,
        // TODO this could be more accurate for some reason.
        timeToKeepOpen: (remainingMs + 1000) / 1000
      })
    }
    return
    // DO NOT add remainingMs to deps array! Creates infinite recursion!
  }, [isWinnersReveal, canShowCards, openSheet, onCloseSheetCallback])

  useEffect(() => {
    if (isPracticeGame) {
      if (isNewHand && latestHand) {
        console.log('!!New winning hand detected:', { isNewHand, handNumber, isUserWinner })
        if (isUserWinner) triggerLocalCelebration(isUserWinner)
      }
    }
  }, [isNewHand, latestHand, handNumber, isUserWinner, isPracticeGame, triggerLocalCelebration])

  useEffect(() => {
    if (!(isNewHand && latestHand && advisorEnabled && advisorFeedbackEnabled)) return

    if (!myPlayer?.userId) return
    // The advisor recaps the hand outcome, not chip flow: a side-pot-only
    // winner lost to the main-pot winner, so it is a loss here even though
    // `isUserWinner` (used for the celebration) is true.
    const advisorWon = didUserWinHand(latestHand, myPlayer.userId)
    const outcome = advisorWon ? 'win' : 'loss'
    const foldedPreflopOnly = didPlayerOnlyFoldPreflop(latestHand.actions, myPlayer.userId)
    // Resolution describes how the HAND ended, not how the hero's result came
    // about: any "Win by fold" means no showdown happened, whoever took the
    // pot. (A hero loss can still be a fold-out — everyone folded to someone
    // else — and that is not a showdown.)
    const handEndedByFolds = latestHand.winners.some((winner) => winner.handRank === 'Win by fold')

    queueHandOutcomeFeedback(
      {
        gameId: game?._id,
        handNumber: latestHand.handNumber,
        userId: myPlayer?.userId
      },
      outcome,
      handEndedByFolds ? 'folds' : 'showdown',
      {
        foldedPreflopOnly,
        wouldHaveWon: foldedPreflopOnly && didFoldedPlayerHaveWinningHand(latestHand, myPlayer.userId)
      }
    )
  }, [
    advisorEnabled,
    advisorFeedbackEnabled,
    game?._id,
    isNewHand,
    latestHand,
    myPlayer?.userId,
    queueHandOutcomeFeedback
  ])

  const renderCelebrations = () => {
    if (!isFocused || renderEmojis.length === 0) return null
    return (
      <View pointerEvents="none" style={[StyleSheet.absoluteFill, { zIndex: 999 }]}>
        {renderEmojis}
      </View>
    )
  }

  const loungeTheme = useMemo(
    () => ({ cardBackColor: loungeTableColor ?? (loungeTableColor || DS.palette['Brand/Maroon']) }),
    [loungeTableColor]
  )

  const requestAdvisorBubbleDismiss = useActionAdvisorStore((state) => state.requestBubbleDismiss)

  return (
    <LoungeThemeContext.Provider value={loungeTheme}>
      <Box style={{ flex: 1 }} onTouchStart={requestAdvisorBubbleDismiss}>
        <Box
          style={{
            flex: 1,
            paddingTop: insets.top,
            backgroundColor: loungeTableColor || DS.palette['Brand/Maroon']
          }}
        >
          <WebWrapper style={{ overflow: 'hidden' }}>
            <Box
              minWidth={'100%'}
              margin={layout.maxWidth > 0 ? 'auto' : undefined}
              _web={{
                paddingTop: layout.padding
              }}
            >
              <StatusBar style="light" />
              <GameConnectionStatusBar />

              <GameDebugPanel />
              <TimingPanel />
              {/* {__DEV__ && (
                <Pressable
                  onPress={() => triggerLocalCelebration(true)}
                  style={{
                    position: 'absolute',
                    bottom: 160,
                    left: 8,
                    zIndex: 100,
                    backgroundColor: 'rgba(255,215,0,0.9)',
                    paddingHorizontal: 10,
                    paddingVertical: 6,
                    borderRadius: 16
                  }}
                >
                  <Text style={{ fontSize: 14, fontWeight: '700' }}>🎉 Test</Text>
                </Pressable>
              )} */}

              {/* {__DEV__ && <GameplayConfigPanel />} */}

              <Content>

                {/*
                  The HUD bar (blinds, time-to-next, table number, waiting overlay,
                  table-move banner) is only relevant to a user who is actively
                  seated or spectating. Busted users without a seat skip it.
                  It mounts INSIDE Content: the HUD (zIndex 50) and the
                  bottom bar (zIndex 97) must be siblings in the same stacking
                  context so the advisor bubble (top: -72 above the bottom bar)
                  paints above the HUD — react-native-web gives every View its
                  own stacking context (position: relative + zIndex: 0 base
                  style), so a z-index on a nested subtree can never beat a
                  sibling ancestor's z-index no matter how high it is.
                */}
                {isDaily10 ? <Daily10HUD /> : !isTournament ? <GameHUD /> : null}
                {isTournament && game?.tournamentId && (myPlayer || isSpectatingServer) && (
                  <TournamentHUD tournamentId={game.tournamentId} userId={myPlayer?.userId ?? contextUserId} />
                )}
                {isPaused && <PauseOverlay />}
                {/* HEADER */}
                <Header />

                {/* MIDDLE - SEATS & BOARD ++  */}
                <MiddleContainer />

                {/* BOTTOM */}
                <BottomContainer />
                <ChatLogsSheet />
              </Content>
            </Box>
            {renderCelebrations()}
          </WebWrapper>

          {/* Teacher controls outside overflow:hidden so panel is not clipped */}
          <TeacherControls />

          {/* Toast for e.g. "Teacher is viewing cards" */}
          <ToastNotification />

          {/* MODALS */}
          <GameModals />

          {/* Teachers join as observers — offer teacher seat / player seat / stay observing */}
          <TeacherSeatChoiceModal />
        </Box>
      </Box>
    </LoungeThemeContext.Provider>
  )
})

function lightenColor(hex: string, amount: number): string {
  const n = parseInt(hex.replace('#', ''), 16)
  const r = Math.min(255, (n >> 16) + Math.round((255 - (n >> 16)) * amount))
  const g = Math.min(255, ((n >> 8) & 0xff) + Math.round((255 - ((n >> 8) & 0xff)) * amount))
  const b = Math.min(255, (n & 0xff) + Math.round((255 - (n & 0xff)) * amount))
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`
}

function darkenColor(hex: string, amount: number): string {
  const n = parseInt(hex.replace('#', ''), 16)
  const r = Math.max(0, Math.round((n >> 16) * (1 - amount)))
  const g = Math.max(0, Math.round(((n >> 8) & 0xff) * (1 - amount)))
  const b = Math.max(0, Math.round((n & 0xff) * (1 - amount)))
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`
}

const TableBorderRadius = 120
const TableEllipse = createComponent(View, {
  backgroundColor: '#812A50',
  borderRadius: TableBorderRadius,
  borderColor: '#0008',
  alignItems: 'center',
  justifyContent: 'center',
  shadowColor: '#260113',
  shadowOffset: { width: 0, height: 0 },
  shadowOpacity: 0.3,
  shadowRadius: 8,
  elevation: 4
})

const TableFelt = createComponent(View, {
  borderRadius: TableBorderRadius,
  borderWidth: 3,
  borderColor: 'rgba(255, 255, 255, 0.1)',
  width: '100%',
  height: '102%',
  transform: [{ scale: 0.96 }]
})

const GameConnectionStatusBar = () => {
  // TODO: add network detection or convex disconnection
  const isConnecting = false

  const color = '#fff'
  const backgroundColor = '#fff5'
  const height = 2
  return (
    <View style={{ height }}>
      <PaperProgressBar
        color={color}
        indeterminate={true}
        visible={isConnecting}
        style={{ height, backgroundColor: backgroundColor }}
      />
    </View>
  )
}

type Player = SanitizedPlayer
type PlayerContainerProps = {
  onPress?: () => void
  player: Player
  alignment?: SeatAlignment
}

//#region PlayerContainer
const PlayerContainer = ({ player, onPress, alignment }: PlayerContainerProps) => {
  const layout = useTableLayoutPosition()
  const ComponentWrapper = onPress ? Pressable : View

  const { currentPlayerId, game } = useGameState()
  const { state: avState } = useAVContext()

  if (!game) {
    throw new Error('GameContainer: PlayerContainer must be used within a GameContainer with a valid game')
  }

  const schedules = useQuery(api.games.queries.getGameSchedules, { gameId: game._id })

  const isPlayerTurn = currentPlayerId === player._id
  const isPlayerWaiting = player.state === 'waiting' && game.state === 'playing'

  const schedule =
    player.kind === 'bot'
      ? schedules?.find((s) => s.scheduleType === 'botAction' && s.playerId === player._id && s.status === 'pending')
      : schedules?.find((s) => s.scheduleType === 'turnTimer' && s.playerId === player._id && s.status === 'pending')

  // TODO: improve animation trigger
  const isAboutToProgressRound = !!schedules?.find(
    (s) => s.scheduleType === 'roundProgression' && s.status === 'pending'
  )

  const playerStyleByState = useMemo(() => {
    if (!player?.state) {
      return undefined
    }
    switch (player.state) {
      case 'out':
      case 'folded':
      case 'sittingout':
        return {
          opacity: 0.9
        }

      default:
        return undefined
    }
  }, [player?.state])

  const playerCards = !(player?.state === 'out' || player?.state === 'waiting') ? player.cards : undefined

  const playerPosition = player?.position !== 'none' ? player?.position : undefined

  const playerDisplayAction = useMemo(() => {
    // Determine action based on player state first
    switch (player.state) {
      case 'waiting':
        // Only show 'waiting' action if game is in progress (late join)
        if (game.state === 'playing') {
          return { action: 'waiting' }
        }
        break
      case 'sittingout':
        return { action: 'sittingout' }
      default:
        break
    }

    // Map blind actions to position-specific labels
    if (player.lastAction?.action === 'blind') {
      switch (player.position) {
        case 'bb':
          return { ...player.lastAction, action: 'BIGBLIND' }
        case 'sb':
          return { ...player.lastAction, action: 'SMALLBLIND' }
      }
    }

    return player.lastAction
  }, [player, game.state])

  const videoConfig = useMemo(() => {
    if (player.kind === 'bot') return undefined
    const playerAgoraUid = userIdToAgoraUid(player.userId)
    const remoteUser = avState.remoteUsers.find((u) => u.uid === playerAgoraUid)
    if (remoteUser?.hasVideo) {
      return { playerId: playerAgoraUid }
    }
    return undefined
  }, [player.kind, player.userId, avState.remoteUsers])

  return (
    <Box
      padding={4}
      style={{
        opacity: ['folded', 'out', 'sittingout'].includes(player.state) || isPlayerWaiting ? 0.5 : 1
      }}
      borderRadius={layout.seats.borderRadius}
    >
      {player.currentBet > 0 && (
        <PlayerBetChips
          alignment={alignment}
          bigBlind={game.bigBlind}
          amount={player.currentBet}
          soundEnable={false}
          enablePotAnimation={isAboutToProgressRound}
        />
      )}
      <PlayerBox
        timerProps={{
          enable: isPlayerTurn && !game.pausedAt && !!schedule,
          timeMS: game.turnDuration,
          startTime: schedule ? schedule.scheduledFor - Date.now() : undefined
        }}
        name={player.name}
        userId={player.userId}
        kind={player.kind}
        skillLevel={player.skillLevel}
        state={player.state}
        action={playerDisplayAction}
        alignment={
          alignment === 'right' ? 'flex-end' : alignment === 'top' || alignment === 'bottom' ? 'center' : undefined
        }
        position={playerPosition}
        chips={player.chips}
        cards={playerCards}
        faceUpCards={player.cards.length > 0 ? [1, 1] : [0, 0]}
        containerStyle={playerStyleByState}
        video={videoConfig}
        avatarBgColor={
          player.kind === 'bot' ? BOT_SKILL_COLORS[player.skillLevel ?? DEFAULT_BOT_SKILL_LEVEL] : undefined
        }
        avatarTextColor={player.kind === 'bot' ? '#7B043D' : undefined}
      />
    </Box>
  )
}

// Rotation for DISPLAY only
const USER_PERSPECTIVE_SEAT_ID = 4

const displayOrderDefaultOptions = {
  totalSeats: 10,
  perspectiveSeatId: USER_PERSPECTIVE_SEAT_ID
}
const getDisplayOrder = (
  players: Player[],
  mySeatId: number,
  { totalSeats, perspectiveSeatId } = displayOrderDefaultOptions
) => {
  // Create array of all seat positions in display order
  // Starting from seat (mySeatId - 4) so mySeatId ends up at position 4
  const displaySeats: (Player | null)[] = Array(totalSeats).fill(null)

  players.forEach((player) => {
    // Calculate display position for this player's seat
    const displayPos = (player.seatId - mySeatId + perspectiveSeatId + totalSeats) % totalSeats
    displaySeats[displayPos] = player
  })

  return displaySeats
}
const EmptySeat = ({ children }: { children: string }) => {
  const layout = useTableLayoutPosition()
  return (
    <Center
      flexGrow={1}
      backgroundColor="#0000000a"
      // backgroundColor="#00000022"
      borderRadius={layout.seats.borderRadius}
    >
      <DS.TextScaled
        variant="Mini/Regular"
        style={{
          color: '#fff9'
        }}
      >
        {children}
      </DS.TextScaled>
    </Center>
  )
}
const UserPlayerSeat = () => {
  const layout = useTableLayoutPosition()
  const { myPlayer, isMyTurn, game, availableActions, isPaused, isPlaying, callAmount } = useGameState()

  const lastAction = useMemo(() => {
    if (!(isPlaying && myPlayer)) {
      return
    }
    return game?.currentHandActions.findLast((a) => a.playerId === myPlayer._id && a.boardState === game.boardState)
  }, [isPlaying, myPlayer, game])

  const schedules = useQuery(api.games.queries.getGameSchedules, game?._id ? { gameId: game._id } : 'skip')

  // TODO: improve animation trigger
  const isAboutToProgressRound = !!schedules?.find(
    (s) => s.scheduleType === 'roundProgression' && s.status === 'pending'
  )

  return (
    <Box marginTop={32}>
      {myPlayer && myPlayer.currentBet > 0 && (
        <PlayerBetChips
          alignment={'bottom'}
          bigBlind={game?.bigBlind || 100}
          amount={myPlayer.currentBet}
          soundEnable={false}
          enablePotAnimation={isAboutToProgressRound}
        />
      )}
    </Box>
  )
}

//#region Table LayoutConfig
const layoutConfig = {
  emptySeats: true
}

//#region MiddleContainer
const MiddleContainer = observer(() => {
  const { game, myPlayer, players, teacher, loungeTableColor } = useGameState()
  const insets = useSafeAreaInsets()
  const layout = useTableLayoutPosition()

  const { isPlayerSeated, bigBoard, showLogoAndBorder } = useGameDevStore()
  const {
    session: { user }
  } = useStore()

  const isAdmin = !!user?.userRoles?.includes('admin')
  // Check if empty seats should be shown (admin + game.showEmptySeats toggle)
  const shouldShowEmptySeats = isAdmin && (game?.showEmptySeats ?? false)
  const isUserPlayingSeated = isPlayerSeated && !!myPlayer
  // const isUserPlayingSeated = useMemo(() => {
  //   // TODO: REMOVE dev store usage
  //   return isPlayerSeated
  // }, [isPlayerSeated])

  const playersBySeat = useMemo(() => {
    if (!(isUserPlayingSeated && myPlayer)) {
      const displaySeats: (Player | null)[] = Array(9).fill(null)
      players.forEach((player) => {
        displaySeats[player.seatId] = player
      })
      return displaySeats
    }

    const seatPlayerRotation = getDisplayOrder(players, myPlayer.seatId)
    return seatPlayerRotation
  }, [isUserPlayingSeated, players, myPlayer])

  const renderSeatPlayer: SeatContentFunction = (seatId, alignment) => {
    if (playersBySeat.length === 0) {
      return null
    }
    // get player by seat
    const player = playersBySeat.at(seatId)
    if (!player) {
      if (layoutConfig.emptySeats && shouldShowEmptySeats) {
        return <EmptySeat>{`Seat ${seatId + 1}`}</EmptySeat>
      }
      return null
    }
    // If current player is playing > don't render that seat
    if (isUserPlayingSeated && myPlayer?._id === player._id) {
      // Render another seat for that user perspective
      return <UserPlayerSeat />
    }

    return <PlayerContainer player={player} alignment={alignment} />
  }

  const availableHeight = layout.windowSize.height - layout.bottomControls.height - layout.header.height - insets.top
  const tableContentHeight = layout.seatsLayout.height - layout.seatsLayout.topSpacer
  const heightDiff = availableHeight - tableContentHeight
  const pushContent = heightDiff - layout.header.height - layout.seatsLayout.topSpacer
  const pushContentTop = pushContent > 0 ? pushContent * 0.5 : 0

  return (
    <View
      style={[
        {
          flex: 4 / 5,
          flexGrow: 1,
          marginTop: pushContentTop,
          alignContent: 'center',
          alignItems: 'center',
          justifyContent: 'center'
        }
      ]}
    >
      {/* SEATS */}
      <Box zIndex={12} pointerEvents="box-none" {...StyleSheet.absoluteFillObject}>
        {/* Host for the advisor avatar. Mounted before the seats so the avatar
            (teleported here by ActionAdvisorCard) renders BELOW them: seated
            players' status ("fold", blinds, bet amounts) stays readable on top
            of the avatar. The speech bubble stays above everything, in the
            bottom bar subtree. */}
        <PortalHost name={ADVISOR_AVATAR_HOST} />
        <PlayerSeats seatContent={renderSeatPlayer} preset="none" />
      </Box>
      {/* TABLE */}
      <Box zIndex={11} marginTop={layout.seatsLayout.topSpacer / 2} paddingTop={layout.seatsLayout.topSpacer / 2}>
        <Center zIndex={11} flexGrow={1}>
          <TableContainer />
        </Center>
        <Box zIndex={0} pointerEvents="none" {...StyleSheet.absoluteFillObject}>
          <Center>
            <TableEllipse
              style={{
                width: bigBoard ? layout.table.width : layout.innerTable.width + layout.seats.width,
                height: tableContentHeight,
                backgroundColor: lightenColor(loungeTableColor || '#812A50', 0.2)
              }}
            >
              {showLogoAndBorder && (
                <TableFelt>
                  <Center marginTop={layout.seatsLayout.topSpacer}>
                    {/* Logo: shown alone or faded behind teacher */}
                    <PokerPowerLogo
                      width={181}
                      height={37}
                      fill={darkenColor(loungeTableColor || '#812A50', 0.15)}
                      opacity={teacher ? 0.35 : 0.6}
                      style={{ transform: [{ scale: 0.9 }] }}
                    />
                    {teacher ? (
                      <Center style={{ marginTop: -layout.seatsLayout.topSpacer * 0.9 }}>
                        <TeacherSeat />
                      </Center>
                    ) : null}
                  </Center>
                </TableFelt>
              )}
            </TableEllipse>
          </Center>
        </Box>
      </Box>
      {/* SidePot BottomSheet */}
      <SidePotSheet />
      <SidePotWinnersSheet />
    </View>
  )
})

//#region TeacherBottomBar – Pause + Teacher Controls when seated as teacher
const TeacherBottomBar = () => {
  const { gameId } = useGameContext()
  const { game, isPaused, isPlaying } = useGameState()
  const pauseGame = useMutation(api.games.mutations.pauseGame)
  const resumeGame = useMutation(api.games.mutations.resumeGame)
  const { showTeacherControls, toggleTeacherControls } = useGamePanelsStore()
  const layout = useTableLayoutPosition()

  const canPauseOrResume = isPlaying && game?._id && gameId

  const onPauseOrResume = useCallback(() => {
    if (!gameId) return
    if (isPaused) {
      resumeGame({ gameId })
    } else {
      pauseGame({ gameId })
    }
  }, [gameId, isPaused, pauseGame, resumeGame])

  const barButtonHeight = 48
  const blackBg = DS.palette['Global/Black']
  const whiteColor = DS.palette['Global/White']

  return (
    <View
      style={{
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 12,
        paddingHorizontal: layout.padding,
        gap: layout.padding
      }}
    >
      {canPauseOrResume && (
        <TouchableOpacity
          onPress={onPauseOrResume}
          hitSlop={8}
          style={{
            width: barButtonHeight,
            minWidth: barButtonHeight,
            height: barButtonHeight,
            borderRadius: barButtonHeight / 2,
            backgroundColor: blackBg,
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {isPaused ? (
            <PlaySVG iconFill={whiteColor} bgFill={blackBg} />
          ) : (
            <PauseSVG iconFill={whiteColor} bgFill={blackBg} />
          )}
        </TouchableOpacity>
      )}
      <TouchableOpacity
        onPress={toggleTeacherControls}
        hitSlop={8}
        style={{
          width: barButtonHeight,
          height: barButtonHeight,
          borderRadius: barButtonHeight / 2,
          backgroundColor: blackBg,
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <SettingsSVG iconFill={whiteColor} bgFill={blackBg} />
      </TouchableOpacity>
    </View>
  )
}
//#endregion

//#region BottomBarContent
type BottomBarContentProps = {
  isSpectator: boolean
  isTeacherSeated: boolean
  canUseTeacherControls: boolean
}

const ObserverNotice = () => (
  <Center paddingVertical={16} paddingHorizontal={24}>
    <DS.TextScaled variant="Body Medium/Medium" style={{ textAlign: 'center' }}>
      {translate('gameScreen.observerNotice')}
    </DS.TextScaled>
  </Center>
)

const BottomBarContent = ({ isSpectator, isTeacherSeated, canUseTeacherControls }: BottomBarContentProps) => {
  const { game } = useGameState()
  const { gameId, userId: contextUserId } = useGameContext()
  const router = useRouter()
  const unspectateGameMutation = useMutation(api.games.mutations.unspectateGame)
  const { tournamentId: spectatingTournamentId } = useSpectatorStatus(gameId, contextUserId)

  // Spectator exit: always offered to anyone in the spectator role (tournament
  // or cash). Tournament spectators route to the lobby Tables tab — that's
  // where they came from. Cash spectators just pop back. The single egress
  // replaces the old asymmetry where only tournament users had a "Back" button.
  const handleReturnToLobby = useCallback(async () => {
    if (game?._id && contextUserId) {
      try {
        await unspectateGameMutation({ gameId: game._id, userId: contextUserId })
      } catch (error) {
        console.warn('Could not clear spectator status before leaving the game.', error)
      }
    }

    if (spectatingTournamentId) {
      spectatorReturnToTables(router, spectatingTournamentId)
      return
    }

    router.back()
  }, [game?._id, contextUserId, spectatingTournamentId, router, unspectateGameMutation])

  // Seated in the teacher seat (middle) — controls only, no observer notice.
  if (isTeacherSeated) {
    return <TeacherBottomBar />
  }

  // Teacher/admin observing — observer notice + controls.
  if (isSpectator && canUseTeacherControls) {
    return (
      <View>
        <ObserverNotice />
        <TeacherBottomBar />
      </View>
    )
  }

  if (isSpectator) {
    // Tournament spectators came from the lobby Tables tab; cash/community
    // spectators came from the home screen. Label and routing follow the
    // origin: only the tournament case routes back to a "lobby".
    const isTournamentSpectator = !!spectatingTournamentId
    return (
      <Center flexGrow={1} paddingVertical={16} paddingHorizontal={24}>
        <DS.TextScaled variant="Body Medium/Medium" style={{ textAlign: 'center' }}>
          {translate('gameScreen.observerNotice')}
        </DS.TextScaled>
        <View style={{ marginTop: 12, width: '100%' }}>
          <DS.Button
            type="On Light"
            variant="Large/Primary"
            backgroundColor="Brand/Maroon"
            onPress={handleReturnToLobby}
          >
            {translate(isTournamentSpectator ? 'gameScreen.observerBackToLobby' : 'gameScreen.observerLeave')}
          </DS.Button>
        </View>
      </Center>
    )
  }

  return <UserPlayerControls />
}
//#endregion

//#region BottomContainer
const BottomContainer = () => {
  const insets = useSafeAreaInsets()
  const maxWidth = useMaxWidth()
  const layout = useTableLayoutPosition()

  const { isPregame, game, players, isPaused, isMyTurn, isHost, canStartOrResumeGame, isTeacherSeated, myPlayer } =
    useGameState()
  const { gameId, userId: contextUserId } = useGameContext()
  const { isSpectator } = useSpectatorStatus(gameId, contextUserId)

  const { isPlayerSeated, canPlayerResume } = useGameDevStore()
  const userRoles = useStore().session.user?.userRoles ?? []
  const canUseTeacherControls =
    userRoles.includes('teacher') || userRoles.includes('admin') || userRoles.includes('superadmin')

  //#region Sheets handling
  useEffect(() => {
    if (isPaused || !isMyTurn) {
      useGameBottomSheetStore.getState().closeSheet()
    }
  }, [isPaused, isMyTurn])
  //#endregion Sheets handling

  const canStartGame = useMemo(() => {
    // Host or teacher can resume when paused (sheet shows; button disabled in quick if < 2 active)
    if (game?.pausedAt && canStartOrResumeGame) {
      return true
    }
    if (!canPlayerResume) {
      return false
    }
    if ((game?.mode === 'practice' || game?.mode === 'daily10') && game?.pausedAt) {
      return true
    }

    if (!(isPregame && game)) {
      return false
    }
    return players.length >= game.minPlayers
  }, [game, isPregame, players, canPlayerResume, canStartOrResumeGame])

  // Show waiting sheet when host/teacher is waiting for more players (has code, not enough players yet).
  // Two cases surface the invite sheet:
  //   1. Pregame lobby waiting to reach minPlayers.
  //   2. A one-off lounge game that paused because players left — with only the lone player remaining it
  //      behaves like a pending lobby again, so we show the invite sheet to bring people back. The game
  //      auto-resumes once 2 players are seated (see joinGame → maybeAutoResumeOneOffLoungeGame).
  const showWaitingForPlayers = useMemo(() => {
    if (!game?.code || !canStartOrResumeGame) return false
    const minPlayers = game?.minPlayers ?? 2
    if (players.length >= minPlayers) return false
    if (isPregame) return true
    return (
      isPaused &&
      (game?.pausedBy === 'leaveGame_insufficient_players' || game?.pausedBy === 'leaveGame_no_humans_remaining')
    )
  }, [isPregame, isPaused, game?.code, game?.minPlayers, game?.pausedBy, players.length, canStartOrResumeGame])

  // Community games (system host + pregameStartAt): everyone sees StartingInSheet countdown.
  // Normal games: non-host, non-teacher see WaitingForHostSheet.
  const isSystemScheduledGame = !!(game?.hostUserId === 'system' && game?.pregameStartAt)
  const showWaitingForHost = useMemo(() => {
    if (!isPregame || game?.mode === 'practice') return false
    // System-scheduled: everyone waits for countdown (StartingInSheet)
    if (isSystemScheduledGame) return true
    // Normal game: only non-host, non-teacher see waiting sheet
    return !canStartOrResumeGame
  }, [isPregame, game?.mode, canStartOrResumeGame, isSystemScheduledGame])

  const isUserPlayingSeated = useMemo(() => {
    return isPlayerSeated
  }, [isPlayerSeated])

  // `isSpectator` comes from the server query (`useSpectatorStatus`) — true iff
  // the caller is in `game.observerUserIds`, has no `players` row, and is not
  // the seated teacher. Replaces the older client-only derivation that flagged
  // anyone with no seat as a spectator (which produced "Back to Lobby" UI for
  // deep-linked random visitors who'd never called `spectateGame`).
  const showPlayerOrTeacherBottom =
    (!(isPregame || canStartGame) && isUserPlayingSeated) || isTeacherSeated || (isSpectator && canUseTeacherControls)

  const showBottomBar = isSpectator || showPlayerOrTeacherBottom
  const {
    feedback: advisorFeedback,
    canRequestFeedback,
    shouldShowAdvisor: shouldShowAdvisorAvatar,
    advisorContext
  } = useActionAdvisor({
    gameId: game?._id,
    handNumber: game?.handNumber,
    mode: game?.mode,
    userId: myPlayer?.userId
  })
  const {
    analytics: { setEvent }
  } = useStore()

  // "Before I play" mode: fetch the advisor's read when the player's turn
  // starts, so the bubble argues the spot before they act. No-op in the
  // default "After I play" mode.
  useTurnAdvice({
    gameId: game?._id,
    handNumber: game?.handNumber,
    userId: myPlayer?.userId,
    gameMode: game?.mode,
    advisorContext,
    isPlayersTurn: isMyTurn,
    handActionCount: game?.currentHandActions?.length
  })
  // `enabled` is the drawer "Advisor available" switch (shows the avatar);
  // `feedbackEnabled` is the avatar tap (feedback on = color, off = grayscale).
  const feedbackEnabled = useActionAdvisorStore((state) => state.feedbackEnabled)
  const adviceBeforePlay = useActionAdvisorStore((state) => state.adviceBeforePlay)
  const advisorAdvice = useActionAdvisorStore((state) => state.advice)
  const advisorAdviceScope = useActionAdvisorStore((state) => state.adviceScope)
  const advisorDismissRequestId = useActionAdvisorStore((state) => state.dismissRequestId)
  const advisorThumbsUpRequestId = useActionAdvisorStore((state) => state.thumbsUpRequestId)
  // Advice owns the bubble while it is pending (turn start → action); the
  // action grade consumes it inside the store and replaces it in the same
  // update, so there is exactly one message per event. Scoped: advice from
  // another game or hand is never current. A grade that followed shown advice
  // shows no bubble at all — the avatar answers with a thumbs-up instead.
  const adviceIsCurrent = Boolean(
    advisorAdvice &&
      advisorAdviceScope?.gameId === game?._id &&
      advisorAdviceScope?.handNumber === game?.handNumber &&
      advisorAdviceScope?.userId === myPlayer?.userId
  )
  const advisorDisplayFeedback = getDisplayedAdvisorFeedback({
    advice: advisorAdvice,
    adviceCurrent: adviceIsCurrent,
    feedback: advisorFeedback
  })
  const advisorOutcomeFeedback = useActionAdvisorStore((state) => state.outcomeFeedback)
  const advisorWelcome = useActionAdvisorStore((state) => state.welcome)
  const queueAdvisorWelcome = useActionAdvisorStore((state) => state.queueAdvisorWelcome)
  const setFeedbackEnabled = useActionAdvisorStore((state) => state.setFeedbackEnabled)
  const clearAdvisorFeedback = useActionAdvisorStore((state) => state.clear)

  const handleToggleAdvisorFeedback = useCallback(
    (next: boolean) => {
      setFeedbackEnabled(next)
      // Analytics: the avatar-tap mute is the advisor's on/off while playing —
      // the key funnel event for the welcome's teaching job.
      setEvent(next ? events.advisor.unmuted : events.advisor.muted, {
        coachingLevel: getAdvisorCoachingLevel(useActionAdvisorStore.getState()),
        gameMode: game?.mode
      })
      if (!next) {
        clearAdvisorFeedback()
      }
    },
    [setFeedbackEnabled, clearAdvisorFeedback, setEvent, game?.mode]
  )
  // Dismiss the advisor bubble promptly once the hand's winners are being revealed.
  const { isWinnersReveal = false } = useActiveFlowSchedule(game?._id, isPaused) || {}

  // Show a one-time welcome bubble the first time the advisor is available in
  // a session, before any real advice or end-of-hand recap has appeared. The
  // card auto-dismisses it (12s) and real feedback replaces it. Feedback starts
  // muted by default, so the welcome must not depend on `canRequestFeedback` —
  // it is the explainer that tells the player how to turn feedback on.
  const prevAdvisorWelcomeRef = useRef(advisorWelcome)
  useEffect(() => {
    if (showBottomBar && shouldShowAdvisorAvatar && !advisorFeedback && !advisorOutcomeFeedback) {
      queueAdvisorWelcome()
    }
  }, [showBottomBar, shouldShowAdvisorAvatar, advisorFeedback, advisorOutcomeFeedback, queueAdvisorWelcome])
  // Analytics: fire only on the transition (welcome just appeared), not on
  // every effect run — queueAdvisorWelcome is idempotent but re-invoked often.
  useEffect(() => {
    if (advisorWelcome && !prevAdvisorWelcomeRef.current) {
      setEvent(events.advisor.welcome_shown, {
        coachingLevel: getAdvisorCoachingLevel(useActionAdvisorStore.getState()),
        gameMode: game?.mode
      })
    }
    prevAdvisorWelcomeRef.current = advisorWelcome
  }, [advisorWelcome, setEvent, game?.mode])

  const is1280x720Fullscreen = isWeb && layout.windowSize.width >= 1280 && layout.windowSize.height >= 720
  const maxHeight = is1280x720Fullscreen ? (isTeacherSeated ? '10%' : isSpectator ? '20%' : '15%') : '100%'

  // TODO: improve user controls render base on user and game state

  return (
    <View
      style={[
        {
          zIndex: 97,
          width: '100%',
          maxHeight,
          maxWidth
        }
      ]}
    >
      {showWaitingForPlayers && game?.code && <WaitingForPlayersSheet code={game.code} />}
      {showWaitingForHost &&
        (game?.hostUserId === 'system' && game?.pregameStartAt ? (
          <StartingInSheet pregameStartAt={game.pregameStartAt} />
        ) : (
          <WaitingForHostSheet />
        ))}
      {/* Don't show Resume Game sheet when seated as teacher and paused — they use bottom bar only */}
      {canStartGame &&
        game?._id &&
        !showWaitingForPlayers &&
        canStartOrResumeGame &&
        !isSystemScheduledGame &&
        !(isTeacherSeated && isPaused) && <StartGameSheet gameId={game._id} />}
      {/* Player controls, seated teacher bar, or spectator notice (e.g. deep link /game/[gameId] with no seat). */}
      {showBottomBar && (
        <Animated.View
          entering={FadeInDown.duration(250)}
          style={[
            {
              width: '100%',
              paddingBottom: insets.bottom || 16,
              marginBottom: 0,
              backgroundColor:
                isTeacherSeated || (isSpectator && canUseTeacherControls) ? 'transparent' : DS.palette['Brand/Cream']
            },
            isTeacherSeated ? { height: 72 } : isSpectator ? { minHeight: 72 } : layout.bottomControls
          ]}
        >
          {shouldShowAdvisorAvatar ? (
            <ActionAdvisorCard
              // Real advice only when feedback is on; the welcome explainer is
              // shown even while muted so a first-time player learns the toggle.
              // Pending turn-start advice outranks the last action grade; stale
              // advice from a previous hand is dropped (adviceIsCurrent).
              gameMode={game?.mode}
              feedback={(canRequestFeedback ? advisorDisplayFeedback : undefined) ?? advisorWelcome?.feedback}
              showBubbleWhileMuted={Boolean(advisorWelcome)}
              headlineOverride={advisorOutcomeFeedback?.headline ?? advisorWelcome?.headline}
              isPlayersTurn={isMyTurn}
              isHandOver={isWinnersReveal}
              enabled={feedbackEnabled}
              dismissRequestId={advisorDismissRequestId}
              thumbsUpRequestId={advisorThumbsUpRequestId}
              onToggle={handleToggleAdvisorFeedback}
            />
          ) : null}
          <BottomBarContent
            isSpectator={isSpectator}
            isTeacherSeated={isTeacherSeated}
            canUseTeacherControls={canUseTeacherControls}
          />
        </Animated.View>
      )}
      {/* {!(isPregame || canStartGame) && !isUserPlayingSeated && (
        <Animated.View
          entering={FadeInDown.duration(250)}
          style={[
            {
              paddingBottom: insets.bottom || 16,
              marginBottom: 0,
              backgroundColor: 'rgba(246, 242, 233, 1)'
            },
            layout.bottomControls
          ]}
        >
          <Row
            alignItems="center"
            justifyContent="center"
            paddingHorizontal={layout.padding * 0.5}
            paddingTop={layout.padding * 0.5}
            gap={layout.padding}
          >
            <View>
              <Chip icon="chat" onPress={() => console.log('Pressed')}>
                Chat
              </Chip>
            </View>
            <View>
              <Chip icon="history" onPress={() => console.log('Pressed')}>
                History
              </Chip>
            </View>
          </Row>
        </Animated.View>
      )} */}
      <GameBottomSheet key={`sheet-${game?._id}-${game?.handNumber ?? 0}`} />
    </View>
  )
}

//#region UserPlayerControls
const UserPlayerControls = () => {
  const { myPlayer, isMyTurn, game, availableActions, isPaused, isPlaying, callAmount } = useGameState()
  const { onFold, onCall, onCheck, onAllIn } = usePlayerActions(game?._id, myPlayer?.userId)
  const {
    permissions: { isSoundEnabled }
  } = useStore()

  // Track previous values for sound triggers
  const prevHandActionsLength = useRef(game?.currentHandActions?.length ?? 0)

  // Play bet/allin sounds when actions occur
  if (game && isSoundEnabled && game.currentHandActions) {
    const currentActionsLength = game.currentHandActions.length
    if (currentActionsLength > prevHandActionsLength.current) {
      // New action added - check the latest action
      const latestAction = game.currentHandActions[currentActionsLength - 1]
      if (latestAction) {
        if (latestAction.action === 'bet' || latestAction.action === 'raise' || latestAction.action === 'call') {
          playSound('chips')
        } else if (latestAction.action === 'allin') {
          playSound('allIn')
        } else if (latestAction.action === 'fold') {
          playSound('fold')
        } else if (latestAction.action === 'check') {
          playSound('check')
        }
      }
    }
    prevHandActionsLength.current = currentActionsLength
  }

  const showActionsForPlayer = isMyTurn && myPlayer

  const wasMyTurn = useDeferredValue(!showActionsForPlayer)

  // When game is paused, show "Game is paused" for everyone (including host when it's their turn)
  const isGamePaused = isPaused || (game?.state === 'playing' && game?.pausedAt != null)
  const showLastAction = wasMyTurn || (isGamePaused && showActionsForPlayer)

  const lastAction = useMemo(() => {
    if (!(isPlaying && myPlayer)) {
      return
    }
    return game?.currentHandActions.findLast((a) => a.playerId === myPlayer._id && a.boardState === game.boardState)
  }, [isPlaying, myPlayer, game])

  const layout = useTableLayoutPosition()
  const cardHeight = layout.handCard.width

  const handRank = useMemo(() => {
    if (!myPlayer?.cards) {
      return
    }

    const cards = [...myPlayer.cards, ...(game?.communityCards ?? [])]
    if (cards.length === 0) {
      return
    }
    const handData = Hand.solve(cards as CardString[])

    return handData.name
  }, [game?.communityCards, myPlayer?.cards])

  const schedules = useQuery(api.games.queries.getGameSchedules, game?._id ? { gameId: game._id } : 'skip')

  // TODO: improve animation trigger
  const isAboutToProgressRound = !!schedules?.find(
    (s) => s.scheduleType === 'roundProgression' && s.status === 'pending'
  )

  const playerAvailableActions = useMemo(() => {
    return availableActions.map((a) => ({
      type: a,
      amount: a === 'call' ? callAmount : undefined
    }))
  }, [availableActions, callAmount])

  return (
    <>
      <Box>
        {isMyTurn && <PlayerController.PlayerProgressBar />}
        {/* First Row */}
        <Row paddingBottom={0}>
          <Box flex={1} paddingVertical={12} paddingHorizontal={8} justifyContent="center">
            <PlayerController.PlayerTimer />
          </Box>

          {/* Hand Cards */}
          <Box flex={1}>
            {myPlayer?.cards && (
              <Animated.View
                style={{
                  marginTop: cardHeight * -1.25
                }}
                entering={FadeInDown.duration(250)}
                exiting={FadeOutDown.duration(250)}
              >
                {/* {lastAction && isPlayerActionWithAmount(lastAction) && (
                  // Moved to Player Seat
                )} */}
                <Center>
                  <PlayerController.HandCards cards={myPlayer?.cards} isCardDisabled={myPlayer.state === 'folded'} />
                </Center>
                {handRank && (
                  <Animated.View
                    entering={FadeInUp.delay(100).duration(200)}
                    exiting={FadeInDown.duration(250)}
                    style={{
                      flexGrow: 1,
                      alignItems: 'center',
                      marginTop: 12
                    }}
                  >
                    <DS.TextScaled
                      uppercase
                      variant="Caption/Bold"
                      style={{
                        textAlign: 'center',
                        width: '100%'
                      }}
                    >
                      {handRank}
                    </DS.TextScaled>
                  </Animated.View>
                )}
              </Animated.View>
            )}
          </Box>

          <Box paddingVertical={12} paddingHorizontal={8} flex={1}>
            <Row gap={4} alignItems="center" overflow="hidden">
              {myPlayer?.state !== 'out' && (
                <Box>
                  <Row gap={3} alignSelf="flex-end">
                    <DS.Icon name="ChipOutline" color={DS.palette['Global/Black']} size={12} />
                    <DS.TextScaled variant={'Mini/Bold'} color={'Global/Black'}>
                      {formatChipsNumber(myPlayer?.chips || 0)}
                    </DS.TextScaled>
                  </Row>
                  {myPlayer && myPlayer?.currentBet > 0 && (game?.showCurrentBets ?? false) && (
                    <Row alignSelf="flex-end" marginTop={2}>
                      <DS.TextScaled variant={'Mini/Regular'} color={'Brand/Purple'}>
                        In pot: {formatChipsNumber(myPlayer.currentBet)}
                      </DS.TextScaled>
                    </Row>
                  )}
                </Box>
              )}
              {/* PLAYER BUTTON > D/SB/BB */}
              {myPlayer?.position && myPlayer?.position !== 'none' && (
                <Animated.View entering={SlideInRight.duration(250)}>
                  <PlayerRole role={myPlayer.position.toUpperCase() as PlayerRoleType} />
                </Animated.View>
              )}
            </Row>
          </Box>
        </Row>

        <Box minHeight={cardHeight}>
          {/* {!showActionsForPlayer && isPaused && (
            <Center flexGrow={1}>
              <DS.Text variant="Body Medium/Medium" style={{ textAlign: 'center', lineHeight: 30 }} uppercase>
                ⏸️ Game is paused
              </DS.Text>
            </Center>
          )} */}
          {showActionsForPlayer && !isPaused && (
            <Row
              flexGrow={1}
              justifyContent="center"
              alignItems="center"
              gap={DS.spacing[8]}
              paddingHorizontal={16}
              // NOTE: disable pointer events on paused state
              pointerEvents={isPaused ? 'none' : 'auto'}
            >
              <PlayerActions
                actions={playerAvailableActions}
                disabledActions={
                  game?.mode === 'daily10' &&
                  game?.boardState === 'deal' &&
                  (game?.freePreflopFoldsUsed ?? 0) >= (game?.freePreflopFoldsAllowed ?? DEFAULT_FREE_PREFLOP_FOLDS)
                    ? ['fold']
                    : []
                }
                onPress={(action) => {
                  if (isPaused) {
                    return
                  }
                  switch (action) {
                    case 'check':
                      onCheck()
                      break
                    case 'call':
                      onCall()
                      break
                    case 'fold':
                      onFold()
                      break
                    case 'allin':
                      onAllIn()
                      break
                    case 'bet':
                    case 'raise':
                      useGameBottomSheetStore.getState().openSheet('Bet')
                      break

                    default:
                      break
                  }
                }}
              />
            </Row>
          )}
          {showLastAction && (
            <Center
              paddingVertical={12}
              marginTop={12}
              borderTopColor={DS.palette['Brand/Light Grey']}
              borderTopWidth={0.5}
              borderBottomColor={DS.palette['Brand/Light Grey']}
              borderBottomWidth={0.5}
            >
              <DS.Text variant="Caption/Bold" style={{ textAlign: 'center', width: '100%' }} uppercase>
                {isGamePaused
                  ? translate('common.gameIsPaused')
                  : getLastActionTextForPlayerConvex(myPlayer, lastAction)}
              </DS.Text>
            </Center>
          )}
        </Box>
      </Box>
    </>
  )
}

const Daily10HUD = () => {
  const { game } = useGameState()
  if (game?.mode !== 'daily10') return null

  const countedHands = game.countedHands ?? 0
  const maxHands = game.maxHands ?? DEFAULT_MAX_HANDS
  const freeFoldsUsed = game.freePreflopFoldsUsed ?? 0
  const freeFoldsAllowed = game.freePreflopFoldsAllowed ?? DEFAULT_FREE_PREFLOP_FOLDS
  const freeFoldsRemaining = freeFoldsAllowed - freeFoldsUsed

  return (
    <View
      style={{
        position: 'absolute',
        bottom: 270,
        left: 0,
        right: 0,
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'box-none',
        zIndex: 50
      }}
    >
      <View
        style={{
          backgroundColor: getDSColorWithOpacity('Global/Black', 0.25),
          borderRadius: 8,
          paddingHorizontal: 12,
          paddingVertical: 6,
          minWidth: 150,
          zIndex: 50,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8
        }}
      >
        {/* Left: Hands remaining */}
        <View style={{ alignItems: 'center' }}>
          <DS.TextScaled variant="Heading 5/Medium" color="Global/White">
            {maxHands - countedHands}
          </DS.TextScaled>
          <DS.TextScaled variant="Mini/Regular" style={{ color: '#fff9' }}>
            Hands Left
          </DS.TextScaled>
        </View>
        {/* Divider */}
        <View style={{ width: 1, height: '100%', backgroundColor: '#fff3', alignSelf: 'stretch' }} />
        {/* Right: Pre-flop Folds label + circles */}
        <View style={{ alignItems: 'center', gap: 2 }}>
          <DS.TextScaled
            variant="Mini/Regular"
            style={{ color: '#fff9', textAlign: 'center', marginTop: -2, marginBottom: 2 }}
          >
            Pre-flop{'\n'}Folds
          </DS.TextScaled>
          <View style={{ flexDirection: 'row', gap: 3 }}>
            {Array.from({ length: freeFoldsAllowed }).map((_, i) => (
              <View
                key={i}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: i < freeFoldsRemaining ? '#fff' : 'transparent',
                  borderWidth: 1,
                  borderColor: '#fff9'
                }}
              />
            ))}
          </View>
        </View>
      </View>
    </View>
  )
}

const HeaderLeft = () => {
  const { toggle } = useChatLogsSheet()
  const sidePotSheet = useSidePotSheet()
  const { gameId, userId } = useGameContext()
  const { game } = useGameState()
  const isPrivate = game?.mode === 'private'

  const onPressChat = useThrottledCallback(() => {
    toggle()
  }, 250)

  return (
    <Row gap={0}>
      {isPrivate && <AVButton gameId={gameId} userId={userId} />}
      <TouchableOpacity
        onPress={onPressChat}
        style={{
          maxHeight: 44,
          padding: 8
        }}
      >
        <Icon source={'chat-outline'} size={24} color="#fff" />
      </TouchableOpacity>
    </Row>
  )
}

type HeaderProps = {
  containerStyle?: ViewStyle
  leftComponent?: ReactNode
  centerComponent?: ReactNode
  rightComponent?: ReactNode
}

//#region Header
const Header = () => {
  const { open: openCheatSheet } = useCheatSheet()
  const onOpenCheatSheet = useThrottledCallback(() => {
    openCheatSheet()
  }, 350)

  const {
    session: { isAdmin, isSuperAdmin }
  } = useStore()

  const { isPaused } = useGameState()
  const layout = useTableLayoutPosition()

  const openOptionsDrawer = useThrottledCallback(
    () => {
      useGameOptionsDrawerStore.getState().openDrawer()
    },
    250,
    {
      leading: true
    }
  )

  return (
    <HeaderBase
      leftComponent={<HeaderLeft />}
      centerComponent={<Box />}
      rightComponent={
        <Center flexDirection="row">
          <View style={{paddingRight: 12}}>
            <DiamondCountDisplay
              backgroundColor={DS.palette['Shades/Maroon 2']}
              textColor={DS.palette['Global/White']}
              isPressable={false}
            />
            
          </View>
          {!(isAdmin || isSuperAdmin) && (
            <TouchableOpacity onPress={onOpenCheatSheet}>
              <DS.Center style={{ flexDirection: 'row', gap: 4 }}>
                <DS.Text variant="Caption/Regular" color="Global/White">
                  Key
                </DS.Text>
                <DS.Icon name="Key" size={24} color={'Global/White'} />
              </DS.Center>
            </TouchableOpacity>
          )}
          <View
            style={{
              zIndex: isPaused ? 999 : 1
            }}
          >
            <IconButton
              icon="menu"
              size={layout.header.icon}
              iconColor={'#FFF'}
              onPress={openOptionsDrawer}
              style={{
                backgroundColor: isPaused ? '#fff1' : 'transparent'
              }}
            />
          </View>
        </Center>
      }
    />
  )
}

const HeaderBase = (props: HeaderProps) => {
  const renderLeft = () => props.leftComponent ?? <HeaderPlaceholder />
  const renderCenter = () => props.centerComponent ?? <HeaderPlaceholder />
  const renderRight = () => props.rightComponent ?? <HeaderPlaceholder />

  return (
    <HeaderContainer style={props?.containerStyle}>
      {renderLeft()}
      {renderCenter()}
      {renderRight()}
    </HeaderContainer>
  )
}

const HeaderPlaceholder = createComponent(View)

const _HeaderButton = createComponent(TouchableOpacity, {
  aspectRatio: 1
})

const HeaderContainer = createComponent(View, {
  flex: 0,
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  minHeight: 44,
  maxHeight: 52
})
